import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RefreshCw, Calendar, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';

import AdminStatsCards from '../components/admin/AdminStatsCards';
import AdminCharts from '../components/admin/AdminCharts';
import AdminQuickActions from '../components/admin/AdminQuickActions';
import SystemHealthPanel from '../components/admin/SystemHealthPanel';
import RecentActivityFeed from '../components/admin/RecentActivityFeed';

import { 
  AdminSystemStatsDto, 
  AdminChartsDataDto, 
  SystemHealthDto, 
  RecentSystemActivityDto,
  AdminDashboardFiltersDto 
} from '../types';
import { apiService } from '../services/api';

const AdminDashboardPage: React.FC = () => {
  const [filters, setFilters] = useState<AdminDashboardFiltersDto>({
    dateRange: '30d'
  });
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Fetch system stats
  const { 
    data: systemStats, 
    isLoading: statsLoading, 
    refetch: refetchStats 
  } = useQuery<AdminSystemStatsDto>({
    queryKey: ['admin-system-stats'],
    queryFn: async () => {
      const response = await apiService.getAdminSystemStats();
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch system stats');
      }
      return response.data;
    },
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });

  // Fetch charts data
  const { 
    data: chartsData, 
    isLoading: chartsLoading, 
    refetch: refetchCharts 
  } = useQuery<AdminChartsDataDto>({
    queryKey: ['admin-charts-data', filters],
    queryFn: async () => {
      const response = await apiService.getAdminChartsData(filters);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch charts data');
      }
      return response.data;
    },
  });

  // Fetch system health
  const { 
    data: systemHealth, 
    isLoading: healthLoading, 
    refetch: refetchHealth 
  } = useQuery<SystemHealthDto>({
    queryKey: ['admin-system-health'],
    queryFn: async () => {
      const response = await apiService.getAdminSystemHealth();
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch system health');
      }
      return response.data;
    },
    refetchInterval: 2 * 60 * 1000, // Refresh every 2 minutes
  });

  // Fetch recent activity
  const { 
    data: recentActivity, 
    isLoading: activityLoading, 
    refetch: refetchActivity 
  } = useQuery<RecentSystemActivityDto[]>({
    queryKey: ['admin-recent-activity'],
    queryFn: async () => {
      const response = await apiService.getAdminRecentActivity(20);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch recent activity');
      }
      return response.data;
    },
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });

  const handleRefresh = async () => {
    try {
      await Promise.all([
        refetchStats(),
        refetchCharts(),
        refetchHealth(),
        refetchActivity()
      ]);
      setLastRefresh(new Date());
      toast.success('Dashboard refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh dashboard');
    }
  };

  const handleDateRangeChange = (value: string) => {
    setFilters(prev => ({ ...prev, dateRange: value }));
  };

  const isLoading = statsLoading || chartsLoading || healthLoading || activityLoading;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <RefreshCw className="h-6 w-6 text-gray-900 dark:text-gray-100" />
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Admin Dashboard</h1>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                System overview and administrative controls
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <Select value={filters.dateRange} onValueChange={handleDateRangeChange}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="1y">Last year</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleRefresh} 
                variant="outline" 
                size="sm"
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Last updated: {lastRefresh.toLocaleString()}
          </div>
        </div>

        {/* System Stats Cards */}
        <div className="mb-8">
          <AdminStatsCards 
            stats={systemStats || {
              totalWorkOrders: 0,
              activeUsers: 0,
              openWorkOrders: 0,
              overdueWorkOrders: 0,
              overduePercentage: 0,
              averageResolutionTimeHours: 0,
              totalCompanies: 0,
              activeCompanies: 0,
              totalVendors: 0,
              activeVendors: 0,
              workOrdersThisMonth: 0,
              workOrdersLastMonth: 0,
              monthlyGrowthPercentage: 0,
              usersThisMonth: 0,
              usersLastMonth: 0,
              userGrowthPercentage: 0
            }} 
            isLoading={statsLoading} 
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Charts Section - Takes up 3 columns */}
          <div className="lg:col-span-3 space-y-8">
            <AdminCharts 
              chartsData={chartsData || {
                workOrdersByStatus: [],
                workOrdersTrend: [],
                workOrdersByPriority: [],
                workOrdersByCategory: [],
                companyActivity: [],
                userAgentsByDevice: [],
                topVendors: [],
                topUsers: []
              }} 
              isLoading={chartsLoading} 
            />
            
            {/* Recent Activity - Now under charts and wider */}
            <RecentActivityFeed 
              activities={recentActivity || []} 
              isLoading={activityLoading} 
            />
          </div>

          {/* Sidebar - Takes up 1 column */}
          <div className="space-y-6">
            {/* System Health */}
            <SystemHealthPanel 
              systemHealth={systemHealth || {
                databaseResponseTimeMs: 0,
                activeSessions: 0,
                systemUptime: '0:00:00',
                errorRate24Hours: 0,
                storageUsageBytes: 0,
                failedLoginAttempts24Hours: 0,
                lastBackupDate: new Date().toISOString(),
                isSystemHealthy: true,
                healthWarnings: []
              }} 
              isLoading={healthLoading} 
            />

            {/* Quick Actions */}
            <AdminQuickActions />
          </div>
        </div>

        {/* Additional Metrics Section */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>System Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent-color">
                    {systemStats?.averageResolutionTimeHours.toFixed(1) || '0.0'}h
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Average Resolution Time</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {systemStats?.overduePercentage.toFixed(1) || '0.0'}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Overdue Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {systemStats?.monthlyGrowthPercentage.toFixed(1) || '0.0'}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Monthly Growth</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
